import React from 'react';
import { Box, Typography, Link, Container } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { Citations } from './Citations';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { trackSocialClick } from '../../configuration/analytics';

const contentUseCase = new ContentUseCase();

export const Footer: React.FC = () => {

  const theme = useTheme();
  const { language } = useLanguage();
  const location = useLocation();
  const { socialMedia } = contentUseCase.getAllContent(language);

  const handleSocialClick = (platform: string) => {
    trackSocialClick(platform);
  };

  // Pages that have the ribbon
  const pagesWithRibbon = ['/', '/me-info', '/timeline', '/donate'];

  // Get citations to show based on current page
  const getCitationsForPage = (): string[] | undefined => {
    switch (location.pathname) {
      case '/':
        return ['1']; // Only citation ¹ for homepage (71 million people)
      case '/me-info':
        return undefined; // All citations for ME info page
      case '/donate':
        return ['1', '2', '4', '6']; // donate page
      case '/about':
        return undefined; // about us page (no sources/citations)
      default:
        return []; // No citations for other pages
    }
  };

  const citationsToShow = getCitationsForPage();

  return (
    <Box component="footer">
      {citationsToShow && citationsToShow.length > 0 && <Citations citationNumbers={citationsToShow} />}
      {(!citationsToShow || citationsToShow.length === 0) && location.pathname === '/me-info' && <Citations />}
      <Box
        sx={{
          py: theme.spacing(3),
          px: theme.spacing(2),
          backgroundColor: theme.palette.grey[50],
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: theme.spacing(2),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © 2026 Run for M.E. - Personal awareness campaign
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: theme.spacing(3),
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <Link
                component={RouterLink}
                to="/impressum"
                color="text.secondary"
                underline="hover"
                sx={{ textDecoration: 'none' }}
              >
                {language === 'de' ? 'Impressum' : 'Imprint'}
              </Link>
              <Link
                component={RouterLink}
                to="/privacy"
                color="text.secondary"
                underline="hover"
                sx={{ textDecoration: 'none' }}
              >
                {language === 'de' ? 'Datenschutz' : 'Privacy'}
              </Link>
              <Link
                component={RouterLink}
                to="/cookie-settings"
                color="text.secondary"
                underline="hover"
                sx={{ textDecoration: 'none' }}
              >
                {language === 'de' ? 'Cookie-Einstellungen' : 'Cookie Settings'}
              </Link>
              <Link
                href={socialMedia.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                color="text.secondary"
                underline="hover"
                onClick={() => handleSocialClick('instagram')}
                sx={{ textDecoration: 'none' }}
              >
                Instagram
              </Link>
            </Box>
          </Box>

          {pagesWithRibbon.includes(location.pathname) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: theme.spacing(0.5) }}>
              Light Blue Ribbon: Von MesserWoland - own work created in Inkscape, based on the graphics by Niki K,{' '}
              <a
                href="https://creativecommons.org/licenses/by-sa/3.0/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                CC BY-SA 3.0
              </a>
              ,{' '}
              <a
                href="https://commons.wikimedia.org/w/index.php?curid=1681680"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Wikimedia Commons
              </a>
            </Typography>
          )}
        </Container>
      </Box>
    </Box>
  );
};
